/**
 * Reusable Comment Section Component
 * Can be used on Tasks, Epics, Stories, Projects, Sprints, Retrospectives, and Reports
 */

import React, { useState, useRef } from 'react';
import { Comment, Attachment } from '../types';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Textarea } from './ui/textarea';
import { MessageSquare, Send, Edit2, Trash2, Reply, ThumbsUp, Smile, Megaphone, Newspaper, Building2, Image, ZoomIn, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { generateId } from '../utils/idGenerator';
import ImageLightbox from './ImageLightbox';

interface CommentSectionProps {
  comments: Comment[];
  currentUserId: string;
  currentUserName: string;
  onAddComment: (content: string, parentCommentId?: string, includeInStandup?: boolean, includeInProjectFeed?: boolean, includeInOrgFeed?: boolean, attachments?: Attachment[]) => void;
  onEditComment: (commentId: string, newContent: string, attachments?: Attachment[]) => void;
  onDeleteComment: (commentId: string) => void;
  onAddReaction?: (commentId: string, emoji: string) => void;
  placeholder?: string;
  /** When true, shows a "Include in standup notes" checkbox on the main comment input */
  enableStandupCheckbox?: boolean;
  /** When true, shows a "Share to Project Feed" checkbox on the main comment input */
  enableProjectFeedCheckbox?: boolean;
  /** When true, shows a "Share to Org Feed" checkbox on the main comment input */
  enableOrgFeedCheckbox?: boolean;
  /** When false, hides the delete action for the current user's comments. */
  canDeleteOwnComments?: boolean;
}

export function CommentSection({
  comments = [],
  currentUserId,
  currentUserName,
  onAddComment,
  onEditComment,
  onDeleteComment,
  onAddReaction,
  placeholder = 'Add a comment...',
  enableStandupCheckbox = false,
  enableProjectFeedCheckbox = false,
  enableOrgFeedCheckbox = false,
  canDeleteOwnComments = true,
}: CommentSectionProps) {
  const [newComment, setNewComment] = useState('');
  const [newCommentImages, setNewCommentImages] = useState<string[]>([]);
  const [includeInStandup, setIncludeInStandup] = useState(false);
  const [includeInProjectFeed, setIncludeInProjectFeed] = useState(false);
  const [includeInOrgFeed, setIncludeInOrgFeed] = useState(false);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState<string | null>(null);

  const topLevelComments = comments.filter(c => !c.parentCommentId);
  const getReplies = (commentId: string) => comments.filter(c => c.parentCommentId === commentId);

  const handleAddComment = () => {
    if (!newComment.trim() && newCommentImages.length === 0) return;
    
    const attachments: Attachment[] = newCommentImages.map((src, i) => ({
      id: generateId('att'),
      name: `image-${i + 1}.jpg`,
      url: src,
      type: 'image/jpeg',
      uploadedAt: new Date().toISOString(),
      uploadedBy: currentUserId,
      category: 'image',
    }));

    onAddComment(
      newComment,
      undefined,
      enableStandupCheckbox ? includeInStandup : undefined,
      enableProjectFeedCheckbox ? includeInProjectFeed : undefined,
      enableOrgFeedCheckbox ? includeInOrgFeed : undefined,
      attachments.length > 0 ? attachments : undefined,
    );
    setNewComment('');
    setNewCommentImages([]);
    setIncludeInStandup(false);
    setIncludeInProjectFeed(false);
    setIncludeInOrgFeed(false);
  };

  const handleReply = (parentId: string) => {
    if (!replyContent.trim()) return;
    onAddComment(replyContent, parentId);
    setReplyContent('');
    setReplyingTo(null);
  };

  const handleEdit = (commentId: string) => {
    if (!editContent.trim()) return;
    onEditComment(commentId, editContent);
    setEditingId(null);
    setEditContent('');
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      if (!file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        setNewCommentImages((prev) => [...prev, ev.target?.result as string]);
      };
      reader.readAsDataURL(file);
    });
    if (e.target) e.target.value = '';
  };

  const removeCommentImage = (idx: number) => {
    setNewCommentImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const startEdit = (comment: Comment) => {
    setEditingId(comment.id);
    setEditContent(comment.content);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditContent('');
  };

  const emojis = ['👍', '❤️', '😊', '🎉', '🚀', '👏', '🔥', '💯'];

  const getReactionCount = (comment: Comment, emoji: string) => {
    return comment.reactions?.filter(r => r.emoji === emoji).length || 0;
  };

  const hasUserReacted = (comment: Comment, emoji: string) => {
    return comment.reactions?.some(r => r.userId === currentUserId && r.emoji === emoji);
  };

  const isImageAttachment = (attachment: { type: string; category?: Attachment['category'] }) => {
    return attachment.category === 'image' || attachment.type.startsWith('image/');
  };

  const renderComment = (comment: Comment, isReply: boolean = false) => {
    const replies = getReplies(comment.id);
    const isEditing = editingId === comment.id;
    const isOwner = comment.createdBy === currentUserId;

    return (
      <div key={comment.id} className={`${isReply ? 'ml-12 mt-3' : 'mt-4'}`}>
        <Card className="shadow-sm border-border bg-muted/40">
          <CardContent className="p-4">
            {/* Comment Header */}
            <div className="flex items-start justify-between mb-2 gap-2">
              <div className="min-w-0">
                <span className="font-medium text-sm">{comment.createdByName}</span>
                <span className="ml-2 text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                </span>
                {comment.edited && (
                  <span className="ml-2 text-xs text-muted-foreground">(edited)</span>
                )}
              </div>
              {isOwner && canDeleteOwnComments && !isEditing && (
                <div className="flex gap-1 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => startEdit(comment)}
                    className="h-8 px-2"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeleteComment(comment.id)}
                    className="h-8 px-2 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Comment Content */}
            {isEditing ? (
              <div className="space-y-2">
                <Textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="min-h-[80px] border-border bg-white text-muted-foreground bg-muted/40"
                />
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" onClick={() => handleEdit(comment.id)} className="h-9">
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={cancelEdit} className="h-9">
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">{comment.content}</p>
              </div>
            )}

            {/* Image Attachments */}
            {comment.attachments && comment.attachments.some(att => isImageAttachment(att))  && (
              <div className="mt-2 grid grid-cols-4 gap-2">
                {comment.attachments
                  .filter(att => isImageAttachment(att))
                  .map((att, i) => (
                    <button
                      key={i}
                      onClick={() => setExpandedImage(att.url)}
                      className="relative group cursor-pointer rounded-lg overflow-hidden hover:ring-2 hover:ring-primary/40 transition-all"
                      aria-label={`View image ${i + 1}`}
                    >
                      <img 
                        src={att.url} 
                        alt={att.name}
                        className="w-full h-20 object-cover rounded-lg group-hover:opacity-80 transition-opacity"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center rounded-lg">
                        <ZoomIn className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </button>
                  ))}
              </div>
            )}

            {/* Reactions */}
            {!isEditing && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {/* Show existing reactions */}
                {comment.reactions && comment.reactions.length > 0 && (
                  <div className="flex gap-1 flex-wrap">
                    {Array.from(new Set(comment.reactions.map(r => r.emoji))).map(emoji => {
                      const count = getReactionCount(comment, emoji);
                      const hasReacted = hasUserReacted(comment, emoji);
                      return (
                        <button
                          key={emoji}
                          onClick={() => onAddReaction?.(comment.id, emoji)}
                          className={`px-2 py-1 text-xs rounded-full border transition-colors h-8 ${
                            hasReacted
                              ? 'bg-primary/10 border-primary/40 text-primary'
                              : 'bg-muted/40 border-border hover:border-border text-muted-foreground dark:hover:border-border'
                          }`}
                        >
                          {emoji} {count}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Emoji picker */}
                {onAddReaction && (
                  <div className="relative">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowEmojiPicker(showEmojiPicker === comment.id ? null : comment.id)}
                      className="h-8 px-2 text-muted-foreground"
                    >
                      <Smile className="h-4 w-4" />
                    </Button>
                    {showEmojiPicker === comment.id && (
                      <div className="absolute left-0 bottom-full z-10 mb-1 flex gap-1 rounded-lg border bg-white p-2 shadow-lg border-border bg-muted/40">
                        {emojis.map(emoji => (
                          <button
                            key={emoji}
                            onClick={() => {
                              onAddReaction(comment.id, emoji);
                              setShowEmojiPicker(null);
                            }}
                            className="rounded p-2 text-lg hover:bg-muted/40 dark:hover:bg-muted/40"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Reply button */}
                {!isReply && !isEditing && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setReplyingTo(replyingTo === comment.id ? null : comment.id);
                      setReplyContent('');
                    }}
                    className="h-8 px-2 text-muted-foreground"
                  >
                    <Reply className="h-4 w-4 mr-1" />
                    Reply
                  </Button>
                )}
              </div>
            )}

            {/* Reply Input */}
            {replyingTo === comment.id && (
              <div className="mt-3 space-y-2">
                <Textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="Write a reply..."
                  className="min-h-[60px] border-border bg-white text-base text-muted-foreground bg-muted/40"
                />
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" onClick={() => handleReply(comment.id)} className="h-9">
                    <Send className="h-4 w-4 mr-1" />
                    Reply
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setReplyingTo(null);
                      setReplyContent('');
                    }}
                    className="h-9"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Image Viewer Modal */}
        {expandedImage && <ImageLightbox imageUrl={expandedImage} onClose={() => setExpandedImage(null)} />}

        {/* Render Replies */}
        {replies.length > 0 && (
          <div className="space-y-0">
            {replies.map(reply => renderComment(reply, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <MessageSquare className="h-5 w-5 text-muted-foreground" />
        <h3 className="text-lg font-semibold text-muted-foreground">
          Comments {comments.length > 0 && `(${comments.length})`}
        </h3>
      </div>

      {/* Add Comment Input */}
      <Card className="border-border bg-muted/40">
        <CardContent className="p-3 sm:p-4">
          <Textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={placeholder}
            className="mb-3 min-h-[80px] border-border bg-white text-base text-muted-foreground bg-muted/40 sm:min-h-[100px]"
          />
          {enableStandupCheckbox && (
            <label className="mb-3 flex h-10 cursor-pointer select-none items-center gap-2 text-sm text-muted-foreground hover:text-muted-foreground dark:hover:text-muted-foreground sm:h-auto">
              <input
                type="checkbox"
                checked={includeInStandup}
                onChange={e => setIncludeInStandup(e.target.checked)}
                className="rounded w-4 h-4"
              />
              <Megaphone className="h-4 w-4 text-chart-4 flex-shrink-0" />
              <span>Include in standup notes</span>
            </label>
          )}
          {enableProjectFeedCheckbox && (
            <label className="mb-3 flex h-10 cursor-pointer select-none items-center gap-2 text-sm text-muted-foreground hover:text-muted-foreground dark:hover:text-muted-foreground sm:h-auto">
              <input
                type="checkbox"
                checked={includeInProjectFeed}
                onChange={e => setIncludeInProjectFeed(e.target.checked)}
                className="rounded w-4 h-4"
              />
              <Newspaper className="h-4 w-4 text-primary flex-shrink-0" />
              <span>Share to project feed</span>
            </label>
          )}
          {enableOrgFeedCheckbox && (
            <label className="mb-3 flex h-10 cursor-pointer select-none items-center gap-2 text-sm text-muted-foreground hover:text-muted-foreground dark:hover:text-muted-foreground sm:h-auto">
              <input
                type="checkbox"
                checked={includeInOrgFeed}
                onChange={e => setIncludeInOrgFeed(e.target.checked)}
                className="rounded w-4 h-4"
              />
              <Building2 className="h-4 w-4 text-chart-2 flex-shrink-0" />
              <span>Share to org feed</span>
            </label>
          )}

          {/* Image Upload Preview */}
          {newCommentImages.length > 0 && (
            <div className="mb-3 grid grid-cols-4 gap-2">
              {newCommentImages.map((src, i) => (
                <div key={i} className="relative group">
                  <button
                    onClick={() => setExpandedImage(src)}
                    className="w-full relative cursor-pointer rounded-lg overflow-hidden hover:ring-2 hover:ring-primary/40 transition-all"
                    aria-label={`View photo ${i + 1}`}
                  >
                    <img 
                      src={src} 
                      alt="" 
                      className="w-full h-16 object-cover rounded-lg group-hover:opacity-80 transition-opacity"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center rounded-lg">
                      <ZoomIn className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                  <button
                    onClick={() => removeCommentImage(i)}
                    className="absolute top-1 right-1 bg-destructive/10 hover:bg-destructive/10 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                    aria-label="Remove photo"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              className="h-10 text-muted-foreground sm:h-9"
            >
              <Image className="h-4 w-4 mr-1.5" />
              Add Image
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handlePhotoSelect}
              aria-label="Upload images to comment"
            />
            <Button onClick={handleAddComment} disabled={!newComment.trim() && newCommentImages.length === 0} className="h-10 sm:h-9">
              <Send className="h-4 w-4 mr-2" />
              Add Comment
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Comments List */}
      {comments.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          No comments yet. Be the first to comment!
        </div>
      ) : (
        <div className="space-y-0">
          {topLevelComments.map(comment => renderComment(comment))}
        </div>
      )}
    </div>
  );
}
